import {FC, useState} from 'react'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useLocation} from 'react-router'
import type {LucideIcon} from 'lucide-react'
import {checkIsActive, ID, KTSVG, WithChildren} from '../../../../helpers'
import {useLayout} from '../../../core'
import {isAccessGranted} from '../../../../../app/helpers/MenuAccessControl'
import {TOKENS} from '../../../../../app/components/design-system'

type Props = {
  menuId: ID
  to: string
  title: string
  icon?: string
  fontIcon?: string
  hasBullet?: boolean
  IconComp?: LucideIcon
}

const SidebarMenuItem: FC<Props & WithChildren> = ({
  menuId,
  children,
  to,
  title,
  icon,
  fontIcon,
  hasBullet = false,
  IconComp,
}) => {
  const {pathname} = useLocation()
  const isActive = checkIsActive(pathname, to)
  const {config} = useLayout()
  const {app} = config
  const menuAccessAllowed = isAccessGranted(menuId).read || isAccessGranted(menuId).write
  const [hover, setHover] = useState(false)

  // Token-driven look. The Metronic .menu-link base class still applies
  // (handles flex layout). Inline styles override its colors/typography
  // so the sidebar reads as part of the redesigned UI instead of a
  // Metronic-out-of-the-box surface.
  const linkBg = isActive
    ? TOKENS.color.actionSoft
    : hover
      ? TOKENS.color.surface
      : 'transparent'
  const linkColor = isActive ? TOKENS.color.action : TOKENS.color.text
  const titleWeight = isActive ? 700 : 500

  return (
    menuAccessAllowed ? (
      <div className='menu-item' style={{ padding: '2px 8px' }}>
        <Link
          className={clsx('menu-link without-sub', {active: isActive})}
          to={to}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            background: linkBg,
            color: linkColor,
            borderRadius: TOKENS.radius.card,
            padding: '8px 12px',
            transition: 'background 120ms ease',
          }}
        >
          {hasBullet && (
            <span className='menu-bullet'>
              <span className='bullet bullet-dot'></span>
            </span>
          )}
          {IconComp ? (
            <span
              className='menu-icon'
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
                color: linkColor,
                opacity: isActive ? 1 : 0.85,
                marginRight: 4,
              }}
            >
              <IconComp size={18} strokeWidth={isActive ? 2.25 : 1.75} />
            </span>
          ) : icon && app?.sidebar?.default?.menu?.iconType === 'svg' && (
            <span className='menu-icon' style={{ color: linkColor, opacity: isActive ? 1 : 0.75 }}>
              {' '}
              <KTSVG path={icon} className='svg-icon-2x' />
            </span>
          )}
          {fontIcon && app?.sidebar?.default?.menu?.iconType === 'font' && (
            <i className={clsx('bi fs-3 mx-3', fontIcon)} style={{ color: linkColor }}></i>
          )}
          <span
            className='menu-title mx-5'
            style={{
              fontSize: 13,
              fontWeight: titleWeight,
              letterSpacing: 0.1,
              color: linkColor,
            }}
          >
            {title}
          </span>
        </Link>
        {children}
      </div>
    ) : (<></>)
  )
}

export {SidebarMenuItem}
